import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export const ArtworkCard = ({ artwork, index = 0 }) => {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--spot-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--spot-y", `${e.clientY - rect.top}px`);
  };

  const isOriginal = artwork.type === "original";

  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/artwork/${artwork.id}`}
        data-testid={`product-card-${artwork.id}`}
        className="group block"
      >
        <div
          ref={ref}
          onMouseMove={onMove}
          className="spotlight-card relative overflow-hidden bg-[#FBF3E2] border border-[rgba(42,33,24,0.07)]"
        >
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={artwork.image_url}
              alt={artwork.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
            />
            {!artwork.is_available && (
              <div className="absolute inset-0 bg-[#F5E8D0]/55 flex items-center justify-center">
                <span
                  data-testid={`sold-badge-${artwork.id}`}
                  className="font-mono-accent text-xs uppercase tracking-[0.35em] text-[#2A2118] border border-[#2A2118]/40 px-5 py-2"
                >
                  Sold
                </span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] bg-gradient-to-t from-[#F5E8D0] via-[#F5E8D0]/70 to-transparent p-5 flex items-center justify-between">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#C9501C]">
                View piece
              </span>
              <ArrowUpRight size={16} className="text-[#C9501C]" />
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <span
                className={`font-mono-accent text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 border ${
                  isOriginal
                    ? "text-[#C9501C] border-[#C9501C]/30"
                    : "text-[#B8860B] border-[#B8860B]/40"
                }`}
              >
                {isOriginal ? "Original · 1 of 1" : "Archival Print"}
              </span>
              <span className="font-mono-accent text-[10px] tracking-widest text-[#AB9C86]">
                {artwork.year}
              </span>
            </div>
            <h3 className="font-serif-display text-xl text-[#2A2118] leading-snug">
              {artwork.title}
            </h3>
            <p className="mt-1 text-xs text-[#7A6C5A]">{artwork.medium}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className="font-mono-accent text-base font-semibold text-[#2A2118]">
                ${artwork.price.toLocaleString()}
              </span>
              <span className="text-[11px] text-[#AB9C86]">{artwork.dimensions.split("(")[0]}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
